import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Root container variants.
 *
 * `variant` controls flex direction (so the `Avatar` lands on the correct
 * side) and the spacing alignment of the bubble column. Visual tokens for
 * the bubble itself live on `chatMessageContentVariants` so the root stays
 * a layout primitive.
 */
export const chatMessageVariants = cva('flex w-full gap-md items-start', {
  variants: {
    variant: {
      sender: 'flex-row-reverse',
      receiver: 'flex-row',
    },
  },
  defaultVariants: {
    variant: 'receiver',
  },
});

/**
 * Container for the message column (bubble + status + timestamp). Stays a
 * column so `Content`, `Status`, and the timestamp stack regardless of the
 * root's horizontal direction.
 */
export const chatMessageBodyVariants = cva('flex flex-col gap-xs min-w-0', {
  variants: {
    variant: {
      sender: 'items-end',
      receiver: 'items-start',
    },
  },
  defaultVariants: {
    variant: 'receiver',
  },
});

export const chatMessageContentVariants = cva(
  'inline-block max-w-full break-words px-md py-xs text-b4',
  {
    variants: {
      variant: {
        sender: 'bg-neutral-100 text-body rounded-lg rounded-br-sm',
        receiver: 'bg-success-500 text-white rounded-lg rounded-bl-sm',
      },
    },
    defaultVariants: {
      variant: 'receiver',
    },
  },
);

/**
 * Status container is a fixed-height row so the layout does not shift when
 * `LoadingDots` swaps for a consumer-supplied retry node.
 */
export const chatMessageStatusVariants = cva(
  'flex items-center gap-xs min-h-[24px]',
  {
    variants: {
      status: {
        idle: '',
        loading: '',
        success: 'text-success-500',
        error: 'text-error-500',
      },
    },
    defaultVariants: {
      status: 'idle',
    },
  },
);

export type ChatMessageVariantProps = VariantProps<typeof chatMessageVariants>;
export type ChatMessageBodyVariantProps = VariantProps<
  typeof chatMessageBodyVariants
>;
export type ChatMessageContentVariantProps = VariantProps<
  typeof chatMessageContentVariants
>;
export type ChatMessageStatusVariantProps = VariantProps<
  typeof chatMessageStatusVariants
>;
